/**
 * How long an emailed sign-in link or code stays usable, in the words we tell
 * customers — shared by both dashboards.
 *
 * WHY THIS IS SHARED: the truth is ONE Supabase project setting,
 * `mailer_otp_exp`, and it governs every emailed credential at once — password
 * reset, magic link, the 6-digit email sign-in code, email-change confirmation,
 * and the gym-owner setup link. Both dashboards send emails that quote that
 * window, so two copies of the number drift the moment one is edited, and the
 * failure is silent and customer-visible: an email that says "Expires: 24 hours
 * from now" directly above "Valid for: 1 hour", which is exactly what the
 * platform-admin welcome email did before this module existed. A reviewer proved
 * the two-file version could drift with every test in both apps still green.
 *
 * The value was 3600 (1 hour) until 2026-08-09, when it was raised to 86400.
 * One hour is a sane rule for someone sitting at their computer having just
 * clicked "forgot password"; it is the wrong rule for a gym owner who reads
 * their approval email that evening. Pink Empire was approved at 7:37pm and the
 * button was dead by 8:37pm.
 *
 * CHANGING IT means changing three things in the same session:
 *   1. `mailer_otp_exp` on the Supabase project (the only real authority),
 *   2. `AUTH_LINK_LIFETIME_HOURS` below,
 *   3. the decision paragraph in
 *      docs/solutions/architecture-patterns/expired-auth-link-self-serve-landing-2026-08-06.md
 * plus `otp_expiry` in gym-admin-dashboard/supabase/config.toml so local
 * testing agrees with production.
 *
 * The mobile app keeps its own inline string in
 * `cheervue-mobile-app/app/(auth)/reset-password.tsx` — Metro's resolver cannot
 * reach `shared/`, and an RN-visible import here would breach the repo's
 * React-Native-free root rule. That one line carries a comment pointing back at
 * this file. It is deliberately outside the guard test's reach, because a jest
 * run in the gym app should not depend on the mobile tree.
 *
 * OTHER COPIES OF THE NUMBER, all of them guarded but none of them derived —
 * change these by hand when the value moves (the "CHANGING IT" list above is
 * the full checklist; this is what makes it non-optional):
 *   - `cheervue-mobile-app/app/(auth)/reset-password.tsx` (the string above)
 *   - `gym-admin-dashboard/src/lib/anthropic/knowledge/featureGuide.ts` — the
 *     parent-facing assistant answer says "24 hours" twice; its guardrail test
 *     builds the expected figure from AUTH_LINK_LIFETIME_HOURS, so a stale
 *     entry fails CI rather than quietly misinforming parents
 *   - `gym-admin-dashboard/supabase/config.toml` (`otp_expiry`)
 *   - the four Supabase-hosted templates, which are not in this repo at all
 *
 * ESCAPE HATCH for the guard: a line about a genuinely one-hour thing that is
 * NOT an emailed link (a JWT lifetime, a cache TTL, a lesson length) can be
 * marked `// link-lifetime-ok` with a short reason — same convention as
 * `// demo:date-ok` in the demo-seed guard.
 *
 * Deliberately plain constants and template strings — no classes, no async —
 * so this stays inside what `shared/` can safely hold.
 */

/** Matches `mailer_otp_exp` (86400 seconds) on the CheerVue production project. */
export const AUTH_LINK_LIFETIME_HOURS = 24;

/** The same window in milliseconds, for deadline arithmetic. */
export const AUTH_LINK_LIFETIME_MS = AUTH_LINK_LIFETIME_HOURS * 60 * 60 * 1000;

/** "24 hours" — for "expires in {X}" / "valid for {X}" sentences. */
export const AUTH_LINK_LIFETIME_TEXT = `${AUTH_LINK_LIFETIME_HOURS} hours`;

/** "24 hours from when this email was sent" — the security-card phrasing. */
export const AUTH_LINK_LIFETIME_FROM_SEND = `${AUTH_LINK_LIFETIME_TEXT} from when this email was sent`;

/** "24 hours from now" — the default when a caller supplies no explicit time. */
export const AUTH_LINK_LIFETIME_FROM_NOW = `${AUTH_LINK_LIFETIME_TEXT} from now`;
